/**
 * NOTE: Use Box with Flexbox for layout, NOT Grid
 * Grid creates ugly layouts - prefer Box with display: flex
 */
import React, { useState, useEffect } from 'react';
import {
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Button,
    Typography,
    Box,
    Chip,
    LinearProgress,
    Paper,
    IconButton,
    CircularProgress
} from '@mui/material';
import {
    Close as CloseIcon,
    CheckCircle,
    Error as ErrorIcon,
    HourglassEmpty,
    Warning,
    Info,
    Download as DownloadIcon,
    InsertChart as ChartIcon,
    Image as ImageIcon,
    TableChart as TableIcon,
    Assessment as AssessmentIcon
} from '@mui/icons-material';
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer } from 'recharts';
import axiosDirectApi from 'utils/axiosDirectApi';

const TrainingJobDetailsDialog = ({ open, onClose, job }) => {
    const [artifacts, setArtifacts] = useState([]);
    const [loadingArtifacts, setLoadingArtifacts] = useState(false);

    // Load artifacts when dialog opens
    useEffect(() => {
        const loadArtifacts = async () => {
            if (!open || !job || job.status !== 'completed') {
                return;
            }

            setLoadingArtifacts(true);
            try {
                const response = await axiosDirectApi.get(`/cv/training/jobs/${job.id}/artifacts`);
                setArtifacts(response.data.artifacts || []);
            } catch (error) {
                console.error('Failed to load artifacts:', error);
                setArtifacts([]);
            } finally {
                setLoadingArtifacts(false);
            }
        };

        loadArtifacts();
    }, [open, job]);

    const handleDownloadArtifact = async (filename) => {
        try {
            const response = await axiosDirectApi.get(`/cv/training/jobs/${job.id}/artifacts/${filename}`);
            // Open presigned URL in new tab
            window.open(response.data.url, '_blank');
        } catch (error) {
            console.error('Failed to download artifact:', error);
        }
    };

    const getArtifactIcon = (type) => {
        switch (type) {
            case 'metrics':
                return <TableIcon color="primary" />;
            case 'plot':
            case 'curve':
            case 'confusion_matrix':
                return <ChartIcon color="success" />;
            case 'validation':
            case 'labels':
                return <ImageIcon color="warning" />;
            default:
                return <AssessmentIcon />;
        }
    };

    const getStatusIcon = (status) => {
        switch (status) {
            case 'completed':
                return <CheckCircle color="success" />;
            case 'failed':
                return <ErrorIcon color="error" />;
            case 'running':
                return <HourglassEmpty color="primary" />;
            case 'pending':
                return <Warning color="warning" />;
            default:
                return <Info color="info" />;
        }
    };

    const getStatusColor = (status) => {
        switch (status) {
            case 'completed':
                return 'success';
            case 'failed':
                return 'error';
            case 'running':
                return 'primary';
            case 'pending':
                return 'warning';
            default:
                return 'default';
        }
    };

    // Early return after all hooks
    if (!job) return null;

    return (
        <Dialog
            open={open}
            onClose={onClose}
            maxWidth="lg"
            fullWidth
            PaperProps={{
                sx: {
                    minHeight: '80vh',
                    maxHeight: '90vh'
                }
            }}
        >
            <DialogTitle
                sx={{
                    background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                    color: 'white',
                    py: 2.5
                }}
            >
                <Box display="flex" alignItems="center" justifyContent="space-between">
                    <Box display="flex" alignItems="center" gap={2}>
                        <Box
                            sx={{
                                width: 48,
                                height: 48,
                                borderRadius: 2,
                                bgcolor: 'rgba(255,255,255,0.2)',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center'
                            }}
                        >
                            {getStatusIcon(job.status)}
                        </Box>
                        <Box>
                            <Typography variant="h5" sx={{ fontWeight: 700, mb: 0.5 }}>
                                {job.name}
                            </Typography>
                            <Chip
                                icon={getStatusIcon(job.status)}
                                label={job.status.toUpperCase()}
                                size="small"
                                sx={{
                                    bgcolor: 'rgba(255,255,255,0.25)',
                                    color: 'white',
                                    fontWeight: 600,
                                    '& .MuiChip-icon': { color: 'white' }
                                }}
                            />
                        </Box>
                    </Box>
                    <IconButton onClick={onClose} size="small" sx={{ color: 'white' }}>
                        <CloseIcon />
                    </IconButton>
                </Box>
            </DialogTitle>

            <DialogContent dividers sx={{ bgcolor: '#f8f9fa', p: 3 }}>
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
                    {/* Basic Info Cards */}
                    <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
                        <Box sx={{ flex: '1 1 300px', minWidth: 0 }}>
                            <Paper
                                elevation={0}
                                sx={{
                                    p: 2.5,
                                    height: '100%',
                                    border: '1px solid',
                                    borderColor: 'divider',
                                    borderRadius: 2,
                                    bgcolor: 'white'
                                }}
                            >
                                <Typography variant="caption" color="text.secondary" fontWeight={600}>
                                    MODEL TYPE
                                </Typography>
                                <Typography variant="h6" sx={{ mt: 1, fontWeight: 700, color: 'primary.main' }}>
                                    {job.model_type.toUpperCase()}
                                </Typography>
                                <Chip label={job.framework} size="small" sx={{ mt: 1, fontWeight: 500 }} variant="outlined" />
                            </Paper>
                        </Box>

                        <Box sx={{ flex: '1 1 300px', minWidth: 0 }}>
                            <Paper
                                elevation={0}
                                sx={{
                                    p: 2.5,
                                    height: '100%',
                                    border: '1px solid',
                                    borderColor: 'divider',
                                    borderRadius: 2,
                                    bgcolor: 'white'
                                }}
                            >
                                <Typography variant="caption" color="text.secondary" fontWeight={600}>
                                    CREATED
                                </Typography>
                                <Typography variant="body1" sx={{ mt: 1, fontWeight: 600 }}>
                                    {new Date(job.created_at).toLocaleDateString('vi-VN')}
                                </Typography>
                                <Typography variant="caption" color="text.secondary">
                                    {new Date(job.created_at).toLocaleTimeString('vi-VN')}
                                </Typography>
                            </Paper>
                        </Box>

                        <Box sx={{ flex: '1 1 300px', minWidth: 0 }}>
                            <Paper
                                elevation={0}
                                sx={{
                                    p: 2.5,
                                    height: '100%',
                                    border: '1px solid',
                                    borderColor: 'divider',
                                    borderRadius: 2,
                                    bgcolor: 'white'
                                }}
                            >
                                <Typography variant="caption" color="text.secondary" fontWeight={600}>
                                    {job.completed_at ? 'COMPLETED' : 'DURATION'}
                                </Typography>
                                {job.completed_at ? (
                                    <>
                                        <Typography variant="body1" sx={{ mt: 1, fontWeight: 600 }}>
                                            {new Date(job.completed_at).toLocaleDateString('vi-VN')}
                                        </Typography>
                                        <Typography variant="caption" color="text.secondary">
                                            {new Date(job.completed_at).toLocaleTimeString('vi-VN')}
                                        </Typography>
                                    </>
                                ) : (
                                    <Typography variant="body1" sx={{ mt: 1, fontWeight: 600, color: 'warning.main' }}>
                                        In Progress
                                    </Typography>
                                )}
                            </Paper>
                        </Box>
                    </Box>

                    {/* Progress */}
                    {job.status === 'running' && (
                        <Paper
                            elevation={0}
                            sx={{
                                p: 3,
                                border: '1px solid',
                                borderColor: 'primary.light',
                                borderRadius: 2,
                                bgcolor: 'white'
                            }}
                        >
                            <Typography variant="subtitle1" fontWeight={600} gutterBottom sx={{ color: 'primary.main' }}>
                                Training Progress
                            </Typography>
                            <Box display="flex" alignItems="center" gap={2} mt={2}>
                                <Box flex={1}>
                                    <LinearProgress
                                        variant="determinate"
                                        value={job.progress || 0}
                                        sx={{
                                            height: 10,
                                            borderRadius: 2,
                                            bgcolor: 'rgba(103, 126, 234, 0.1)',
                                            '& .MuiLinearProgress-bar': {
                                                borderRadius: 2,
                                                background: 'linear-gradient(90deg, #667eea 0%, #764ba2 100%)'
                                            }
                                        }}
                                    />
                                </Box>
                                <Typography variant="h6" fontWeight={700} sx={{ minWidth: 60, color: 'primary.main' }}>
                                    {Math.round(job.progress || 0)}%
                                </Typography>
                            </Box>
                        </Paper>
                    )}

                    {/* Hyperparameters */}
                    {job.hyperparameters && Object.keys(job.hyperparameters).length > 0 && (
                        <Paper
                            elevation={0}
                            sx={{
                                p: 3,
                                border: '1px solid',
                                borderColor: 'divider',
                                borderRadius: 2,
                                bgcolor: 'white'
                            }}
                        >
                            <Typography variant="subtitle1" fontWeight={600} gutterBottom sx={{ color: 'text.primary', mb: 2 }}>
                                Hyperparameters
                            </Typography>
                            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2 }}>
                                {Object.entries(job.hyperparameters).map(([key, value]) => (
                                    <Box
                                        key={key}
                                        sx={{
                                            flex: '1 1 200px',
                                            minWidth: 0,
                                            p: 2,
                                            borderRadius: 1.5,
                                            bgcolor: '#f8f9fa',
                                            border: '1px solid',
                                            borderColor: 'divider'
                                        }}
                                    >
                                        <Typography
                                            variant="caption"
                                            color="text.secondary"
                                            fontWeight={600}
                                            sx={{ textTransform: 'uppercase' }}
                                        >
                                            {key.replace(/_/g, ' ')}
                                        </Typography>
                                        <Typography variant="body1" fontWeight={600} sx={{ mt: 0.5, wordBreak: 'break-word' }}>
                                            {JSON.stringify(value)}
                                        </Typography>
                                    </Box>
                                ))}
                            </Box>
                        </Paper>
                    )}

                    {/* Metrics */}
                    {job.metrics && Object.keys(job.metrics).length > 0 && (
                        <Paper
                            elevation={0}
                            sx={{
                                p: 3,
                                border: '1px solid',
                                borderColor: 'divider',
                                borderRadius: 2,
                                bgcolor: 'white'
                            }}
                        >
                            <Typography variant="subtitle1" fontWeight={600} gutterBottom sx={{ color: 'text.primary', mb: 2 }}>
                                Final Metrics
                            </Typography>
                            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2 }}>
                                {Object.entries(job.metrics).map(([key, value]) => (
                                    <Box
                                        key={key}
                                        sx={{
                                            flex: '1 1 180px',
                                            minWidth: 0,
                                            p: 2.5,
                                            borderRadius: 2,
                                            bgcolor: 'linear-gradient(135deg, rgba(103, 126, 234, 0.05) 0%, rgba(118, 75, 162, 0.05) 100%)',
                                            border: '2px solid',
                                            borderColor: 'primary.light',
                                            textAlign: 'center'
                                        }}
                                    >
                                        <Typography
                                            variant="caption"
                                            color="text.secondary"
                                            fontWeight={600}
                                            sx={{ textTransform: 'uppercase' }}
                                        >
                                            {key.replace(/_/g, ' ')}
                                        </Typography>
                                        <Typography variant="h5" fontWeight={700} sx={{ mt: 1, color: 'primary.main' }}>
                                            {typeof value === 'number' ? value.toFixed(3) : value}
                                        </Typography>
                                    </Box>
                                ))}
                            </Box>
                        </Paper>
                    )}

                    {/* Training History Chart */}
                    {job.metrics_history && job.metrics_history.length > 0 && (
                        <Paper
                            elevation={0}
                            sx={{
                                p: 3,
                                border: '1px solid',
                                borderColor: 'divider',
                                borderRadius: 2,
                                bgcolor: 'white'
                            }}
                        >
                            <Typography variant="subtitle1" fontWeight={600} gutterBottom sx={{ color: 'text.primary', mb: 3 }}>
                                Training History
                            </Typography>
                            <Box sx={{ bgcolor: '#fafbfc', p: 2, borderRadius: 1.5 }}>
                                <ResponsiveContainer width="100%" height={350}>
                                    <LineChart data={job.metrics_history}>
                                        <CartesianGrid strokeDasharray="3 3" stroke="#e0e0e0" />
                                        <XAxis
                                            dataKey="epoch"
                                            label={{ value: 'Epoch', position: 'insideBottom', offset: -5, style: { fontWeight: 600 } }}
                                            stroke="#666"
                                        />
                                        <YAxis
                                            label={{ value: 'Score', angle: -90, position: 'insideLeft', style: { fontWeight: 600 } }}
                                            domain={[0, 1]}
                                            stroke="#666"
                                        />
                                        <Tooltip
                                            formatter={(value) => value.toFixed(4)}
                                            labelFormatter={(label) => `Epoch ${label}`}
                                            contentStyle={{
                                                backgroundColor: 'rgba(255, 255, 255, 0.95)',
                                                border: '1px solid #ddd',
                                                borderRadius: 8,
                                                boxShadow: '0 4px 12px rgba(0,0,0,0.1)'
                                            }}
                                        />
                                        <Legend wrapperStyle={{ paddingTop: 20 }} iconType="line" />
                                        <Line
                                            type="monotone"
                                            dataKey="mAP50"
                                            stroke="#667eea"
                                            strokeWidth={3}
                                            name="mAP@0.5"
                                            dot={{ r: 4, fill: '#667eea' }}
                                            activeDot={{ r: 6 }}
                                        />
                                        <Line
                                            type="monotone"
                                            dataKey="mAP50-95"
                                            stroke="#764ba2"
                                            strokeWidth={3}
                                            name="mAP@0.5:0.95"
                                            dot={{ r: 4, fill: '#764ba2' }}
                                            activeDot={{ r: 6 }}
                                        />
                                        <Line
                                            type="monotone"
                                            dataKey="precision"
                                            stroke="#f093fb"
                                            strokeWidth={3}
                                            name="Precision"
                                            dot={{ r: 4, fill: '#f093fb' }}
                                            activeDot={{ r: 6 }}
                                        />
                                        <Line
                                            type="monotone"
                                            dataKey="recall"
                                            stroke="#4facfe"
                                            strokeWidth={3}
                                            name="Recall"
                                            dot={{ r: 4, fill: '#4facfe' }}
                                            activeDot={{ r: 6 }}
                                        />
                                    </LineChart>
                                </ResponsiveContainer>
                            </Box>
                        </Paper>
                    )}

                    {/* Error Message */}
                    {job.error_message && (
                        <Paper
                            elevation={0}
                            sx={{
                                p: 3,
                                border: '2px solid',
                                borderColor: 'error.main',
                                borderRadius: 2,
                                bgcolor: '#fff5f5'
                            }}
                        >
                            <Box display="flex" alignItems="center" gap={1.5} mb={2}>
                                <ErrorIcon color="error" fontSize="large" />
                                <Typography variant="subtitle1" fontWeight={700} color="error.main">
                                    Error Message
                                </Typography>
                            </Box>
                            <Box
                                sx={{
                                    p: 2,
                                    bgcolor: 'white',
                                    borderRadius: 1.5,
                                    border: '1px solid',
                                    borderColor: 'error.light'
                                }}
                            >
                                <Typography
                                    variant="body2"
                                    sx={{
                                        fontFamily: 'monospace',
                                        whiteSpace: 'pre-wrap',
                                        color: 'error.dark',
                                        lineHeight: 1.6
                                    }}
                                >
                                    {job.error_message}
                                </Typography>
                            </Box>
                        </Paper>
                    )}

                    {/* Training Artifacts */}
                    {job.status === 'completed' && (
                        <Paper
                            elevation={0}
                            sx={{
                                p: 3,
                                border: '1px solid',
                                borderColor: 'divider',
                                borderRadius: 2,
                                bgcolor: 'white'
                            }}
                        >
                            <Typography variant="subtitle1" fontWeight={600} gutterBottom sx={{ color: 'text.primary', mb: 2 }}>
                                Training Artifacts
                            </Typography>
                            {loadingArtifacts ? (
                                <Box display="flex" justifyContent="center" alignItems="center" py={6}>
                                    <CircularProgress size={40} thickness={4} />
                                </Box>
                            ) : artifacts.length === 0 ? (
                                <Box
                                    sx={{
                                        textAlign: 'center',
                                        py: 4,
                                        bgcolor: '#f8f9fa',
                                        borderRadius: 1.5,
                                        border: '1px dashed',
                                        borderColor: 'divider'
                                    }}
                                >
                                    <AssessmentIcon sx={{ fontSize: 48, color: 'text.disabled', mb: 1 }} />
                                    <Typography variant="body2" color="text.secondary">
                                        No artifacts available
                                    </Typography>
                                </Box>
                            ) : (
                                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2 }}>
                                    {artifacts.map((artifact) => (
                                        <Box
                                            key={artifact.filename}
                                            sx={{
                                                flex: '1 1 280px',
                                                minWidth: 0,
                                                p: 2,
                                                border: '1px solid',
                                                borderColor: 'divider',
                                                borderRadius: 1.5,
                                                bgcolor: '#fafbfc',
                                                transition: 'all 0.2s',
                                                '&:hover': {
                                                    borderColor: 'primary.main',
                                                    bgcolor: 'white',
                                                    boxShadow: '0 4px 12px rgba(103, 126, 234, 0.15)',
                                                    transform: 'translateY(-2px)'
                                                }
                                            }}
                                        >
                                            <Box display="flex" alignItems="flex-start" gap={1.5}>
                                                <Box sx={{ mt: 0.5 }}>{getArtifactIcon(artifact.type)}</Box>
                                                <Box flex={1} sx={{ minWidth: 0 }}>
                                                    <Typography
                                                        variant="body2"
                                                        fontWeight={600}
                                                        sx={{
                                                            overflow: 'hidden',
                                                            textOverflow: 'ellipsis',
                                                            whiteSpace: 'nowrap'
                                                        }}
                                                    >
                                                        {artifact.filename}
                                                    </Typography>
                                                    <Typography variant="caption" color="text.secondary">
                                                        {artifact.type} • {(artifact.size / 1024).toFixed(1)} KB
                                                    </Typography>
                                                </Box>
                                                <IconButton
                                                    onClick={() => handleDownloadArtifact(artifact.filename)}
                                                    size="small"
                                                    sx={{
                                                        color: 'primary.main',
                                                        '&:hover': {
                                                            bgcolor: 'primary.lighter'
                                                        }
                                                    }}
                                                >
                                                    <DownloadIcon fontSize="small" />
                                                </IconButton>
                                            </Box>
                                        </Box>
                                    ))}
                                </Box>
                            )}
                        </Paper>
                    )}
                </Box>
            </DialogContent>

            <DialogActions sx={{ px: 3, py: 2, bgcolor: '#f8f9fa' }}>
                <Button
                    onClick={onClose}
                    variant="contained"
                    sx={{
                        px: 4,
                        py: 1,
                        borderRadius: 2,
                        fontWeight: 600,
                        textTransform: 'none',
                        background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                        '&:hover': {
                            background: 'linear-gradient(135deg, #5568d3 0%, #63408b 100%)',
                            boxShadow: '0 4px 12px rgba(103, 126, 234, 0.4)'
                        }
                    }}
                >
                    Close
                </Button>
            </DialogActions>
        </Dialog>
    );
};

export default TrainingJobDetailsDialog;
